import React, { useState, useEffect } from 'react';
import { useSocket, persistPlayerSeat } from '../contexts/SocketContext';
import { useTranslations } from '../hooks/useTranslations';
import { translateError, type Room } from '@deadpan/shared';

export const RoomJoining: React.FC = () => {
  const { socket, isConnected, error, currentRoom, adoptRoom } = useSocket();
  const [roomCode, setRoomCode] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [isJoining, setIsJoining] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const t = useTranslations();

  const relevantError = !currentRoom && (localError || (error && (error.includes('join') || error.includes('Room not found') || error.includes('duplicate') || error.includes('already in the room') || error.includes('full')) ? error : null));

  const joinGameRoom = async () => {
    if (!socket || !isConnected || !roomCode.trim() || !playerName.trim()) {
      return;
    }

    const normalizedRoomCode = roomCode.trim().toUpperCase();
    const normalizedPlayerName = playerName.trim();

    const joinedRoom = await new Promise<Room | null>((resolve) => {
      socket.emit('client:join-room', normalizedRoomCode, normalizedPlayerName, (room) => {
        resolve(room);
      });
    });

    if (!joinedRoom) {
      return;
    }

    setLocalError(null);
    persistPlayerSeat(joinedRoom, normalizedPlayerName);
    adoptRoom(joinedRoom, normalizedPlayerName);
  };

  const handleJoinRoom = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!socket || !isConnected || !roomCode.trim() || !playerName.trim()) {
      return;
    }

    setLocalError(null);
    setIsJoining(true);
    try {
      await joinGameRoom();
    } finally {
      setIsJoining(false);
    }
  };

  useEffect(() => {
    const joinErrors = ['join', 'Room not found', 'duplicate', 'already in the room', 'full', 'Player name is required'];
    setLocalError(error && joinErrors.some(e => error.includes(e)) ? error : null);
    if (currentRoom) {
      setLocalError(null);
    }
  }, [error, currentRoom]);

  return (
    <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-4 sm:p-6 md:p-8 max-w-md w-full border border-white/20">
      <div className="mb-4 sm:mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent mb-2">
          {t.ui.joinRoom}
        </h2>
        <p className="text-xs sm:text-sm text-gray-600">{t.ui.enterRoomCode}</p>
      </div>
      <form onSubmit={handleJoinRoom} className="space-y-5">
        <div>
          <label htmlFor="joinRoomCode" className="block text-sm font-semibold text-gray-700 mb-2">
            {t.ui.roomCode}
          </label>
          <input
            id="joinRoomCode"
            type="text"
            value={roomCode}
            onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
            placeholder="ABC123"
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all outline-none uppercase font-mono text-center text-lg tracking-widest"
            required
            disabled={!isConnected || isJoining}
            maxLength={6}
            pattern="[A-Z0-9]{6}"
          />
        </div>
        <div>
          <label htmlFor="joinPlayerName" className="block text-sm font-semibold text-gray-700 mb-2">
            {t.ui.yourName}
          </label>
          <input
            id="joinPlayerName"
            type="text"
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            placeholder={t.ui.enterYourName}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all outline-none"
            required
            disabled={!isConnected || isJoining}
            maxLength={20}
          />
        </div>
        {relevantError && (
          <div className="text-red-700 text-sm bg-red-50 border border-red-200 p-3 rounded-xl">
            {translateError(relevantError)}
          </div>
        )}
        <button
          type="submit"
          disabled={!isConnected || isJoining || !roomCode.trim() || !playerName.trim()}
          className="w-full bg-gradient-to-r from-pink-600 to-rose-600 text-white py-3 px-4 rounded-xl font-semibold hover:from-pink-700 hover:to-rose-700 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
        >
          {isJoining ? t.ui.joining : `🎮 ${t.ui.joinRoom}`}
        </button>
        {!isConnected && (
          <p className="text-sm text-gray-500 text-center">{t.ui.connectingToServer}</p>
        )}
      </form>
    </div>
  );
};
